import { type ErrorReport } from './interfaces';

/**
 * Per-surface report context — the ONLY part of an {@link ErrorReport} that
 * genuinely differs between products, which is why each surface used to carry
 * its own `buildReport`.
 *
 * The user input (free text + attachments) is identical everywhere; what varies
 * is the *context* the surface can capture about where the report was filed:
 *
 * - **AI Tutor lesson** (personal / test / talks) — `roomHash`, `stepUuid`,
 *   `stepIndex`, `contentLessonId`, and a resolved `category` from the slide.
 * - **UAT** — `roomHash` + the test's `contentLessonId`, no step/category.
 * - **Mic-check** — `roomHash` only (no slide yet).
 * - **Math trainer** — `contentLessonId` (task id) + `mathContext` task ids.
 * - **Student / reports cabinet** — nothing lesson-shaped (all empty).
 *
 * Each field is optional and defaults to the empty/absent form the proxy treats
 * as "not applicable" (`''` → `null`, omitted → absent), so a surface only
 * supplies the slice it actually has. Resolving that slice — especially the
 * dynamic `category` (depends on the submitted `categories[0]`) — stays with the
 * surface, which owns the category list and the lesson state; assembling it into
 * a report is shared here (see {@link buildErrorReport}).
 */
export interface ErrorReportContext {
  /** AI Tutor lesson room hash; omit / `''` on non-lesson surfaces. */
  roomHash?: string;
  stepUuid?: string;
  stepIndex?: number;
  contentLessonId?: number | string;
  /** Resolved category key; omit / `''` when the surface has no category picker. */
  category?: string;
  /** Math-trainer task identifiers, forwarded verbatim to `context.mathContext`. */
  mathContext?: Record<string, unknown>;
  /** Product-specific diagnostic fields forwarded under proxy clientMeta. */
  clientMeta?: Record<string, unknown>;
}

/** The subset of the modal's send-data the common assembly reads. */
export interface ErrorReportUserInput {
  message?: string;
  resourceUrls?: string[];
  resourceNames?: string[];
}

/**
 * Assemble an {@link ErrorReport} from the modal's user input plus the surface's
 * {@link ErrorReportContext}. Centralises the parts that are the SAME for every
 * product (the message + the attachment url/name pairing) so a new common field
 * lands in one place instead of every surface's hand-rolled `buildReport`; the
 * genuinely per-product context stays with the surface that can capture it.
 */
export function buildErrorReport(input: ErrorReportUserInput, context: ErrorReportContext = {}): ErrorReport {
  return {
    message: input.message ?? '',
    resourceUrls: input.resourceUrls ?? [],
    // Original client filenames aligned by index with resourceUrls.
    resourceNames: input.resourceNames ?? [],
    roomHash: context.roomHash ?? '',
    stepUuid: context.stepUuid ?? '',
    category: context.category ?? '',
    ...(context.stepIndex !== undefined ? { stepIndex: context.stepIndex } : {}),
    ...(context.contentLessonId !== undefined ? { contentLessonId: context.contentLessonId } : {}),
    ...(context.mathContext ? { mathContext: context.mathContext } : {}),
    ...(context.clientMeta ? { clientMeta: context.clientMeta } : {}),
  };
}
